import { Injectable, computed, effect, inject, signal, untracked } from '@angular/core';
import { debounceTime, fromEvent, map, startWith } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService } from '../../shared/services/auth.service';
import { hasAccess } from '../constants/roles';

export interface Menu {
  headTitle1?: string;
  level?: number;
  headTitle2?: string;
  path?: string;
  title?: string;
  type?: string;
  icon?: string;
  active?: boolean;
  bookmark?: boolean;
  pinnedData?: boolean;
  items?: Menu[];
  children?: Menu[];
}

const ALL_MENU_CHILDREN: (Menu & { routeSegment: string })[] = [
  {
    routeSegment: 'profilo',
    title: 'Users Profile',
    icon: 'profile-sidebar',
    type: 'link',
    active: false,
    path: '/dashboard/profilo'
  },
  {
    routeSegment: 'pazienti',
    title: 'patient_list.title',
    icon: 'pazienti',
    type: 'link',
    active: false,
    path: '/dashboard/pazienti'
  },
  {
    routeSegment: 'chat-pazienti',
    title: 'patient_list.chat_patient',
    icon: 'chat',
    type: 'link',
    active: false,
    path: '/dashboard/chat-pazienti'
  },
  {
    routeSegment: 'medici',
    title: 'doctor.doctor_list',
    icon: 'medici',
    type: 'link',
    active: false,
    path: '/dashboard/medici'
  },
  {
    routeSegment: 'utenti-app',
    title: 'doctor.user_app_list',
    icon: 'pazienti',
    type: 'link',
    active: false,
    path: '/dashboard/utenti-app'
  },
  {
    routeSegment: 'configurazioni',
    title: 'configurazioni.title',
    icon: 'configurazioni',
    type: 'link',
    active: false,
    path: '/dashboard/configurazioni'
  },
  {
    routeSegment: 'operatorie',
    title: 'surgical_list.title',
    icon: 'calendario',
    type: 'link',
    active: false,
    path: '/dashboard/operatorie'
  },
  {
    routeSegment: 'calendario',
    title: 'calendar.title',
    icon: 'calendar',
    type: 'link',
    active: false,
    path: '/dashboard/calendario'
  },
  {
    routeSegment: 'comunicazioni',
    title: 'communication.title',
    icon: 'comunicazioni',
    type: 'link',
    active: false,
    path: '/dashboard/comunicazioni'
  }
];

@Injectable({
  providedIn: 'root'
})
export class NavService {
  private readonly authService = inject(AuthService);

  // 1. Larghezza schermo reattiva
  public screenWidth = toSignal(
    fromEvent(window, 'resize').pipe(
      debounceTime(100),
      map(() => window.innerWidth),
      startWith(window.innerWidth)
    ),
    { initialValue: window.innerWidth }
  );

  // 2. Stato sidebar (signal per permettere il toggle manuale)
  public collapseSidebar = signal(window.innerWidth < 1200);

  // 3. MENU REATTIVO (Pure Projection)
  public items = computed<Menu[]>(() => {
    const role = this.authService.userRole();
    if (!role) return [];

    // Filtra per ruolo
    const children = ALL_MENU_CHILDREN.filter(({ routeSegment }) => hasAccess(role, routeSegment));

    // Filtra se password non aggiornata
    const visibleChildren = this.authService.passwordUpdateRequired()
      ? children.filter((item) => item.path === '/dashboard/profilo')
      : children;

    return visibleChildren.map((item) => ({ ...item, level: 1 }));
  });

  constructor() {
    this.initializePasswordUpdateRequired();

    // Aggiorna la sidebar automaticamente al cambio di risoluzione
    effect(() => {
      const width = this.screenWidth();
      untracked(() => {
        this.collapseSidebar.set(width < 1200);
      });
    });
  }

  private initializePasswordUpdateRequired() {
    const user = this.authService.getUser();
    if (user && !user.password_updated) {
      this.authService.passwordUpdateRequired.set(true);
    }
  }

  public refreshMenu() {
    // Non fa più nulla di manuale, items() è un computed!
  }
}
